import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { removeLoadingKey } from '../../helpers/common'
import { toast } from 'react-toastify';

export const NotificationsLoadingKeys = {
    getNotifications: "GET_NOTIFICATIONS",
    markAllAsRead: "MARK_ALL_AS_READ",
    markAsRead: "MARK_AS_READ_UNREAD",
    getNotificationTypes: "GET_NOTIFICATION_TYPES",
    updateNotificationType: "UPDATE_NOTIFICATION_TYPE",
    sendNotificationTypePreview: "SEND_NOTIFICATION_TYPE_PREVIEW",
    getNotificationTypePreferences: "GET_NOTIFICATION_TYPE_PREFERENCES",
    updateNotificationTypePreferences: "UPDATE_NOTIFICATION_TYPE_PREFERENCES"
}

const initialState = {
    sidebarNotifications: {
        list: [],
        totalCount: 0,
        unreadCount: 0,
    },
    pageNotifications: {
        list: [],
        totalCount: 0,
        unreadCount: 0,
    },
    notificationTypes: [],
    notificationTypesError: null,
    notificationTypePreferences: {},
    loadingKeys: [],
    loading: false,
}

export const getNotificationsRequest = createAsyncThunk(
  'notifications/getNotificationsRequest',
  async ({ userId, all = false }, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/management/notifications`, { params: { userId, all } });
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const markAllAsReadRequest = createAsyncThunk(
  'notifications/markAllAsReadRequest',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/management/notifications/mark-all-as-read`, { userId });
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const markAsReadUnreadRequest = createAsyncThunk(
  'notifications/markAsReadUnreadRequest',
  async ({ userId, notificationId, readed }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/management/notifications/mark-as-read-unread`, { userId, notificationId, readed });
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const markBulkAsReadUnreadRequest = createAsyncThunk(
  'notifications/markBulkAsReadUnreadRequest',
  async ({ userId, notificationIds, readed }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/management/notifications/mark-bulk-as-read-unread`, { userId, notificationIds, readed });
      return { data, notificationIds, readed };
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response.data.errors })
      }
    }
  }
)

export const getNotificationTypePreferencesRequest = createAsyncThunk(
  'notifications/getNotificationTypePreferencesRequest',
  async (_arg, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/management/notification-types/preferences`);
      return data;
    } catch (error) {
      if ('string' === typeof error.response?.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response?.data?.error || error.response?.data?.errors })
      }
    }
  }
)

export const updateNotificationTypePreferencesRequest = createAsyncThunk(
  'notifications/updateNotificationTypePreferencesRequest',
  async ({ preferences }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`/api/v1/management/notification-types/preferences`, { preferences });
      return data;
    } catch (error) {
      if ('string' === typeof error.response?.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response?.data?.error || error.response?.data?.errors })
      }
    }
  }
)

export const getNotificationTypesRequest = createAsyncThunk(
  'notifications/getNotificationTypesRequest',
  async (_arg, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/management/notification-types`);
      return data;
    } catch (error) {
      if ('string' === typeof error.response?.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response?.data?.error || error.response?.data?.errors })
      }
    }
  }
)

export const updateNotificationTypeRequest = createAsyncThunk(
  'notifications/updateNotificationTypeRequest',
  async ({ code, changes }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`/api/v1/management/notification-types/${code}`, changes);
      return data;
    } catch (error) {
      if ('string' === typeof error.response?.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response?.data?.error || error.response?.data?.errors })
      }
    }
  }
)

export const sendNotificationTypePreviewRequest = createAsyncThunk(
  'notifications/sendNotificationTypePreviewRequest',
  async ({ code, email, subject, body_html, provenance_note }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `/api/v1/management/notification-types/${code}/preview-email`,
        { email, subject, body_html, provenance_note }
      );
      return data;
    } catch (error) {
      if ('string' === typeof error.response?.data) {
        return rejectWithValue({ error: error.response.data })
      } else {
        return rejectWithValue({ error: error.response?.data?.error || error.response?.data?.errors })
      }
    }
  }
)

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    cleanUpNotificationsData: (state) => {
      state.sidebarNotifications = { list: [], totalCount: 0, unreadCount: 0 };
      state.pageNotifications = { list: [], totalCount: 0, unreadCount: 0 };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getNotificationsRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, NotificationsLoadingKeys.getNotifications];
      })
      .addCase(getNotificationsRequest.fulfilled, (state, action) => {
        state.loading = false;
        const isAll = !!action?.meta?.arg?.all;
        const target = isAll ? 'pageNotifications' : 'sidebarNotifications';
        state[target].list = action.payload?.items || [];
        state[target].totalCount = action.payload?.totalCount || 0;
        state[target].unreadCount = action.payload?.unreadCount || 0;
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.getNotifications, state.loadingKeys);
      })
      .addCase(getNotificationsRequest.rejected, (state, action) => {
        state.loading = false;
        const isAll = !!action?.meta?.arg?.all;
        const target = isAll ? 'pageNotifications' : 'sidebarNotifications';
        state[target] = { list: [], totalCount: 0, unreadCount: 0 };
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.getNotifications, state.loadingKeys);
        // toast.error(
        //   `Notifications can't be fetched. Error - ${action.error?.message}.`, { position: "bottom-right" }
        // )
      })
      .addCase(getNotificationTypePreferencesRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, NotificationsLoadingKeys.getNotificationTypePreferences];
      })
      .addCase(getNotificationTypePreferencesRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.notificationTypePreferences = action.payload?.preferences || {};
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.getNotificationTypePreferences, state.loadingKeys);
      })
      .addCase(getNotificationTypePreferencesRequest.rejected, (state) => {
        state.loading = false;
        state.notificationTypePreferences = {};
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.getNotificationTypePreferences, state.loadingKeys);
      })
      .addCase(updateNotificationTypePreferencesRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, NotificationsLoadingKeys.updateNotificationTypePreferences];
      })
      .addCase(updateNotificationTypePreferencesRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.updateNotificationTypePreferences, state.loadingKeys);
        (action?.meta?.arg?.preferences || []).forEach(({ code, in_app, slack_dm, email }) => {
          state.notificationTypePreferences[code] = { in_app, slack_dm, email };
        });
      })
      .addCase(updateNotificationTypePreferencesRequest.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.updateNotificationTypePreferences, state.loadingKeys);
        const reason = action.payload?.error || action.error?.message;
        toast.error(
          `Your delivery preference can't be saved. Error - ${reason}.`, { position: "bottom-right" }
        )
      })
      .addCase(getNotificationTypesRequest.pending, (state) => {
        state.loading = true;
        state.notificationTypesError = null;
        state.loadingKeys = [...state.loadingKeys, NotificationsLoadingKeys.getNotificationTypes];
      })
      .addCase(getNotificationTypesRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.notificationTypes = action.payload?.notificationTypes || [];
        state.notificationTypesError = null;
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.getNotificationTypes, state.loadingKeys);
      })
      .addCase(getNotificationTypesRequest.rejected, (state, action) => {
        state.loading = false;
        state.notificationTypes = [];
        state.notificationTypesError = action.payload?.error || action.error?.message || 'Failed to fetch notification types';
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.getNotificationTypes, state.loadingKeys);
      })
      .addCase(updateNotificationTypeRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, NotificationsLoadingKeys.updateNotificationType];
      })
      .addCase(updateNotificationTypeRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.updateNotificationType, state.loadingKeys);
        const updated = action.payload?.notificationType;
        if (updated?.code) {
          state.notificationTypes = (state.notificationTypes || []).map(
            (type) => (type.code === updated.code ? updated : type)
          );
        }
      })
      .addCase(updateNotificationTypeRequest.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.updateNotificationType, state.loadingKeys);
        const reason = action.payload?.error || action.error?.message;
        toast.error(
          `Notification settings can't be saved. Error - ${reason}.`, { position: "bottom-right" }
        )
      })
      .addCase(sendNotificationTypePreviewRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, NotificationsLoadingKeys.sendNotificationTypePreview];
      })
      .addCase(sendNotificationTypePreviewRequest.fulfilled, (state) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(
          NotificationsLoadingKeys.sendNotificationTypePreview,
          state.loadingKeys
        );
      })
      .addCase(sendNotificationTypePreviewRequest.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(
          NotificationsLoadingKeys.sendNotificationTypePreview,
          state.loadingKeys
        );
        const reason = action.payload?.error || action.error?.message;
        toast.error(
          `Notification preview can't be sent. Error - ${reason}.`, { position: "bottom-right" }
        )
      })
      .addCase(markAsReadUnreadRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, NotificationsLoadingKeys.markAsRead];
      })
      .addCase(markAsReadUnreadRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.markAsRead, state.loadingKeys);
        // Optimistically update both lists based on request args
        const notificationId = action?.meta?.arg?.notificationId;
        const nextReaded = String(action?.meta?.arg?.readed) === 'true';
        const updateList = (list) => {
          return (list || []).map((item) => {
            if (item.notification_id === notificationId || item.id === notificationId) {
              return { ...item, readed: nextReaded };
            }
            return item;
          });
        };
        state.sidebarNotifications.list = updateList(state.sidebarNotifications.list);
        state.pageNotifications.list = updateList(state.pageNotifications.list);
        state.sidebarNotifications.unreadCount = (state.sidebarNotifications.list || []).filter(n => !n.readed).length;
        state.pageNotifications.unreadCount = (state.pageNotifications.list || []).filter(n => !n.readed).length;
      })
      .addCase(markAsReadUnreadRequest.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.markAsRead, state.loadingKeys);
        toast.error(
          `Notification can't be marked as read/unread. Error - ${action.error?.message}.`, { position: "bottom-right" }
        )
      })
      .addCase(markAllAsReadRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, NotificationsLoadingKeys.markAllAsRead];
      })
      .addCase(markAllAsReadRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.markAllAsRead, state.loadingKeys);
        state.sidebarNotifications.list = (state.sidebarNotifications.list || []).map(n => ({ ...n, readed: true }));
        state.pageNotifications.list = (state.pageNotifications.list || []).map(n => ({ ...n, readed: true }));
        state.sidebarNotifications.unreadCount = 0;
        state.pageNotifications.unreadCount = 0;
        toast.success(`All notifications marked as read successfully.`, { position: "bottom-right" })
      })
      .addCase(markAllAsReadRequest.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.markAllAsRead, state.loadingKeys);
        toast.error(
          `All notifications can't be marked as read. Error - ${action.error?.message}.`, { position: "bottom-right" }
        )
      })
      .addCase(markBulkAsReadUnreadRequest.pending, (state) => {
        state.loading = true;
        state.loadingKeys = [...state.loadingKeys, NotificationsLoadingKeys.markAsRead];
      })
      .addCase(markBulkAsReadUnreadRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.markAsRead, state.loadingKeys);
        const ids = action?.meta?.arg?.notificationIds || [];
        const nextReaded = String(action?.meta?.arg?.readed) === 'true' || action?.meta?.arg?.readed === true;
        const updateList = (list) => {
          return (list || []).map((item) => {
            if (ids.includes(item.notification_id) || ids.includes(item.id)) {
              return { ...item, readed: nextReaded };
            }
            return item;
          });
        };
        state.sidebarNotifications.list = updateList(state.sidebarNotifications.list);
        state.pageNotifications.list = updateList(state.pageNotifications.list);
        state.sidebarNotifications.unreadCount = (state.sidebarNotifications.list || []).filter(n => !n.readed).length;
        state.pageNotifications.unreadCount = (state.pageNotifications.list || []).filter(n => !n.readed).length;
        toast.success(`Selected notifications updated.`, { position: "bottom-right" })
      })
      .addCase(markBulkAsReadUnreadRequest.rejected, (state, action) => {
        state.loading = false;
        state.loadingKeys = removeLoadingKey(NotificationsLoadingKeys.markAsRead, state.loadingKeys);
        toast.error(
          `Selected notifications can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
        )
      })
  }
})

export const { cleanUpNotificationsData } = notificationsSlice.actions;

export default notificationsSlice.reducer
